import { useState } from "react";
import { createPortal } from "react-dom";
import { Download, Loader2, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { SIGN_FACTS, cap } from "@/lib/signFacts";

const W = 1080, H = 1920;
const GLYPH = { aries: "♈", taurus: "♉", gemini: "♊", cancer: "♋", leo: "♌", virgo: "♍", libra: "♎", scorpio: "♏", sagittarius: "♐", capricorn: "♑", aquarius: "♒", pisces: "♓" };

function wrapLines(ctx, text, max) {
  const out = [];
  let line = "";
  for (const w of text.split(/\s+/)) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > max && line) { out.push(line); line = w; } else line = t;
  }
  if (line) out.push(line);
  return out;
}

const hook = (text) => (text.match(/[^.!?]+[.!?]+/g) || [text]).slice(0, 2).join(" ").trim();

// Draws a 1080×1920 story card for a sign pair and returns a PNG blob.
async function drawCard(a, b, text) {
  await document.fonts?.ready;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const x = c.getContext("2d");
  x.fillStyle = "#04060B"; x.fillRect(0, 0, W, H);
  const g = x.createRadialGradient(W / 2, 760, 50, W / 2, 760, 900);
  g.addColorStop(0, "rgba(212,175,55,0.22)"); g.addColorStop(1, "rgba(4,6,11,0)");
  x.fillStyle = g; x.fillRect(0, 0, W, H);
  for (let i = 0; i < 160; i++) {
    x.fillStyle = `rgba(247,244,238,${0.15 + ((i * 37) % 70) / 100})`;
    x.beginPath(); x.arc((i * 997) % W, (i * 613) % H, ((i * 7) % 3) + 1, 0, Math.PI * 2); x.fill();
  }
  x.strokeStyle = "#D4AF37"; x.lineWidth = 4; x.strokeRect(48, 48, W - 96, H - 96);
  x.textAlign = "center";
  x.fillStyle = "#D4AF37"; x.font = "600 40px Cinzel, Georgia, serif"; x.fillText("A S T R A", W / 2, 190);
  x.font = "36px 'Cormorant Garamond', Georgia, serif"; x.fillStyle = "#E8DCC4"; x.fillText("ZODIAC COMPATIBILITY", W / 2, 260);
  x.font = "220px 'Noto Sans Symbols', 'Segoe UI Symbol', serif"; x.fillStyle = "#D4AF37";
  x.fillText(GLYPH[a] + "\uFE0E", W / 2 - 230, 700); x.fillText(GLYPH[b] + "\uFE0E", W / 2 + 230, 700);
  x.font = "120px 'Cormorant Garamond', Georgia, serif"; x.fillStyle = "#F7F4EE"; x.fillText("×", W / 2, 660);
  x.font = "600 92px Cinzel, Georgia, serif"; x.fillStyle = "#F7F4EE";
  x.fillText(cap(a).toUpperCase(), W / 2, 900); x.fillStyle = "#D4AF37"; x.font = "60px 'Cormorant Garamond', Georgia, serif";
  x.fillText("&", W / 2, 990); x.font = "600 92px Cinzel, Georgia, serif"; x.fillStyle = "#F7F4EE"; x.fillText(cap(b).toUpperCase(), W / 2, 1100);
  x.font = "38px 'Cormorant Garamond', Georgia, serif"; x.fillStyle = "#A7B0C0";
  x.fillText(`${SIGN_FACTS[a].element} · ${SIGN_FACTS[b].element}`, W / 2, 1180);
  x.font = "italic 50px 'Cormorant Garamond', Georgia, serif"; x.fillStyle = "#E8DCC4";
  let lines = wrapLines(x, `“${hook(text)}”`, W - 240);
  if (lines.length > 5) lines = wrapLines(x, `“${hook(text).match(/[^.!?]+[.!?]+/)?.[0]?.trim() || hook(text)}”`, W - 240);
  if (lines.length > 5) lines = [...lines.slice(0, 4), `${lines[4].replace(/[”"]$/, "")}…”`];
  lines.forEach((l, i) => x.fillText(l, W / 2, 1320 + i * 66));
  x.fillStyle = "#D4AF37"; x.font = "600 40px Cinzel, Georgia, serif"; x.fillText("ASTRALOGICO.COM", W / 2, H - 170);
  x.font = "34px 'Cormorant Garamond', Georgia, serif"; x.fillStyle = "#A7B0C0"; x.fillText(`/compatibility/${a}-${b}`, W / 2, H - 115);
  return new Promise((res) => c.toBlob(res, "image/png"));
}

export default function PairShareCard({ a, b, text }) {
  const [busy, setBusy] = useState(false);
  const [card, setCard] = useState(null);
  const name = `astra-${a}-${b}-compatibility.png`;
  const open = async () => {
    setBusy(true);
    try {
      const blob = await drawCard(a, b, text);
      setCard({ blob, url: URL.createObjectURL(blob) });
    } catch { toast.error("Couldn't create the card. Please try again."); }
    setBusy(false);
  };
  const close = () => { URL.revokeObjectURL(card.url); setCard(null); };
  const file = card && new File([card.blob], name, { type: "image/png" });
  const canShare = file && navigator.canShare?.({ files: [file] });
  const share = async () => {
    try { await navigator.share({ files: [file], title: `${cap(a)} × ${cap(b)} compatibility`, text: `${cap(a)} × ${cap(b)} on Astra · astralogico.com/compatibility/${a}-${b}` }); }
    catch { /* cancelled */ }
  };
  return (
    <>
      <button onClick={open} disabled={busy || !text} data-testid="btn-pair-share"
        className="mt-4 w-full flex items-center justify-center gap-2 border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 gold-text rounded-full py-3 transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <Share2 size={16} />} Share a {cap(a)} × {cap(b)} card to Instagram or TikTok
      </button>
      {card && createPortal(
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-5 overflow-y-auto" data-testid="pair-share-modal" onClick={close}>
          <div className="w-full max-w-xs flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={close} aria-label="Close" data-testid="pair-share-close" className="self-end mb-2 flex text-[#E8DCC4]"><X size={22} /></button>
            <img src={card.url} alt={`${cap(a)} and ${cap(b)} compatibility card`} data-testid="pair-share-image" className="max-h-[62vh] w-auto rounded-2xl border border-[#D4AF37]/40" />
            <div className="flex gap-2 mt-3 w-full">
              {canShare && <button onClick={share} data-testid="pair-share-native" className="flex-1 flex items-center justify-center gap-2 bg-[#D4AF37] text-[#04060B] font-medium rounded-full py-2.5"><Share2 size={15} /> Share</button>}
              <a href={card.url} download={name} data-testid="pair-share-download" className="flex-1 flex items-center justify-center gap-2 border border-[#D4AF37]/50 gold-text rounded-full py-2.5"><Download size={15} /> Download</a>
            </div>
            <p className="text-xs text-[#A7B0C0] text-center mt-2">Story size for Instagram and TikTok. On iPhone you can also press and hold the image to save it.</p>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
